import { useState } from "react";
import axios from "axios";
import { Reveal } from "../anim";
import { Instagram, Facebook, Linkedin, Mail, X, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Booking } from "./Booking";
import { useSite, t } from "@/lib/site";
import { sendMessage, API } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const { site } = useSite();

  const contactEmail = t(site, "footer.email", "hello@radacollab.com");

  const SOCIALS = [
    { label: "Instagram", icon: Instagram, href: t(site, "footer.instagram_url", "https://www.instagram.com/radacollab/") },
    { label: "Facebook", icon: Facebook, href: t(site, "footer.facebook_url", "https://www.instagram.com/radacollab/") },
    { label: "LinkedIn", icon: Linkedin, href: t(site, "footer.linkedin_url", "#") },
    { label: "Mail", icon: Mail, onClick: () => setContactOpen(true) },
  ];

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) { toast.error("Please fill name, email and message."); return; }
    setSending(true);
    try {
      await sendMessage(contact);
      toast.success("Message sent — we'll get back to you soon.");
      setContact({ name: "", email: "", subject: "", message: "" });
      setContactOpen(false);
    } catch { toast.error("Couldn't send. Please try again."); }
    setSending(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { setEmailError(true); return; }
    setEmailError(false);
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "footer" });
      toast.success("You're on the list. Welcome to Rada Collab.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
    setBusy(false);
  };

  return (
    <footer id="contact" data-testid="footer-section" className="relative">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 pt-24 md:pt-40 pb-12">
        <Reveal>
          <span className="text-sm font-bold uppercase tracking-[0.25em] text-[#FF8473]">
            Join the movement
          </span>
          <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.82] text-[15vw] md:text-[11vw] text-[#1F3864]">
            Experience<br />
            <span className="text-[#00AF95]">Filipino</span> Design.
          </h2>
        </Reveal>

        {/* intro text — same style/weight as the 'Idea to launch' subtext */}
        <Reveal className="mt-12">
          <p className="text-lg md:text-2xl leading-snug text-[#23201C] max-w-4xl">
            Let's create your next collection.
            <br />
            <span className="text-[#23201C]/65 whitespace-nowrap">Book an online consultation or come meet the team at the studio.</span>
          </p>
        </Reveal>

        {/* booking app */}
        <Reveal delay={0.05}>
          <Booking />
        </Reveal>

        {/* subscribe + socials — same row, socials aligned right */}
        <div className="mt-20 flex flex-wrap items-center gap-6">
          <Reveal className="flex-1 min-w-[280px]">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#23201C]/50">
              Stay in the loop
            </p>
            <form onSubmit={submit} noValidate className="mt-5 flex flex-col sm:flex-row gap-3 max-w-md">
              <div className="flex-1 relative">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (e.target.value) setEmailError(false); }}
                  placeholder="you@company.com"
                  className={`w-full bg-transparent border rounded-full px-5 py-3 text-[#23201C] placeholder:text-[#23201C]/40 focus:outline-none transition-colors ${
                    emailError ? "border-[#1F3864] ring-1 ring-[#1F3864]" : "border-[#23201C]/25 focus:border-[#00AF95]"}`}
                />
                {emailError && (
                  <div data-testid="newsletter-error" className="absolute -top-10 left-0 flex items-center gap-2 bg-[#1F3864] text-[#EBE6E0] text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    <AlertCircle size={13} className="shrink-0" />
                    Please fill out this field
                  </div>
                )}
              </div>
              <button
                data-testid="newsletter-submit"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 bg-[#D7E85C] text-[#1F3864] border border-transparent px-6 py-3 rounded-full font-bold uppercase text-sm hover:bg-[#16294B] hover:text-[#EBE6E0] hover:border-[#D7E85C] active:scale-95 transition-[background,transform,color,border-color] duration-200 disabled:opacity-60"
              >
                {busy ? "…" : "Subscribe"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="ml-auto self-end pb-1">
            <div className="flex items-center gap-4">
              {SOCIALS.map(({ label, icon: Icon, href, onClick }) => (
                <div key={label} className="relative">
                  {onClick ? (
                    <button
                      onClick={onClick}
                      aria-label={label}
                      data-testid={`footer-social-${label.toLowerCase()}`}
                      className="grid place-items-center h-11 w-11 rounded-full bg-[#1F3864] text-[#EBE6E0] hover:bg-[#00AF95] active:scale-90 transition-[background,transform] duration-200"
                    >
                      <Icon size={18} />
                    </button>
                  ) : (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={`footer-social-${label.toLowerCase()}`}
                      className="grid place-items-center h-11 w-11 rounded-full bg-[#1F3864] text-[#EBE6E0] hover:bg-[#00AF95] active:scale-90 transition-[background,transform] duration-200"
                    >
                      <Icon size={18} />
                    </a>
                  )}
                  {label === "Mail" && (
                    <div className="absolute top-full right-0 mt-1.5 pointer-events-none select-none" aria-hidden>
                      <svg width="46" height="28" viewBox="0 0 46 28" className="mx-auto block" fill="none" stroke="#1F3864" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 24 Q 24 30 41 7" />
                        <path d="M35.1 7.9 L 41 7 L 41.9 12.9" />
                      </svg>
                      <span className="block whitespace-nowrap font-display font-bold uppercase tracking-[0.25em] text-sm text-[#23201C]/50 mt-0.5" data-testid="contact-us-label">
                        Contact Us
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-24 pt-8 border-t border-[#23201C]/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#23201C]/60">
          <img src="/rada-logo-coal.png" alt="Rada Collab" className="h-8 w-auto" />
          <span>#fromFilipinostotheWorld</span>
          <span>© {new Date().getFullYear()} Rada Collab. Crafted for Impact.</span>
        </div>
      </div>

      {/* Contact popup */}
      {contactOpen && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-[#1F3864]/70 backdrop-blur-sm p-4" onClick={() => !sending && setContactOpen(false)}>
          <form data-testid="contact-form" onClick={(e) => e.stopPropagation()} onSubmit={submitContact}
            className="rounded-2xl w-full max-w-md p-6 bg-[#F4F1EC] border-2 border-[#1F3864] text-[#1F3864]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-2xl text-[#1F3864]">Email Us</h3>
              <button type="button" onClick={() => setContactOpen(false)} className="text-[#1F3864]/50 hover:text-[#1F3864]"><X size={20} /></button>
            </div>
            <p className="text-xs text-[#1F3864]/60 mb-5">{contactEmail}</p>
            <div className="space-y-3">
              <input data-testid="contact-name" required placeholder="Your name" value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="w-full bg-[#1F3864] border border-[#1F3864] rounded-md px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
              <input data-testid="contact-email" required type="email" placeholder="you@company.com" value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="w-full bg-[#1F3864] border border-[#1F3864] rounded-md px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
              <input data-testid="contact-subject" placeholder="Subject" value={contact.subject}
                onChange={(e) => setContact({ ...contact, subject: e.target.value })}
                className="w-full bg-[#1F3864] border border-[#1F3864] rounded-md px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
              <textarea data-testid="contact-message" required rows={4} placeholder="How can we help?" value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="w-full bg-[#1F3864] border border-[#1F3864] rounded-md px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C] resize-none" />
              <button data-testid="contact-submit" disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D7E85C] text-[#1F3864] font-bold uppercase py-3 rounded-full hover:bg-[#EBE6E0] active:scale-[0.98] transition-[background,transform] disabled:opacity-60">
                {sending ? "Sending…" : <>Send Message <Send size={16} /></>}
              </button>
            </div>
          </form>
        </div>
      )}
    </footer>
  );
};
